import { useLoyalty } from '../../../contexts/LoyaltyContext';

interface LoyaltyTierProgressProps {
  showGlow?: boolean;
  showNextRewardCard?: boolean;
  layout?: 'horizontal' | 'compact';
  nextRewardTitle?: string;
  nextRewardDescription?: string;
}

function LoginCta() {
  return (
    <div className="loyalty-card p-6 text-center">
      <div className="text-3xl mb-2 opacity-50">&#127942;</div>
      <p className="text-[var(--color-text-muted)] text-sm">
        <a href="/login" className="text-[var(--color-primary)] hover:underline font-semibold">Sign in</a>
        {' '}to track your tier progress.
      </p>
    </div>
  );
}

export function LoyaltyTierProgressBlock({ props }: { props: LoyaltyTierProgressProps }) {
  const { data, loading } = useLoyalty();
  const showGlow = props.showGlow ?? true;
  const showNextRewardCard = props.showNextRewardCard ?? true;
  const layout = props.layout ?? 'horizontal';

  if (loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-4 w-40 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="h-3 w-full bg-[var(--color-surface-border)] rounded-full" />
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const { tierProgress } = data;
  const percent = Math.min(100, Math.max(0, tierProgress.progressPercent));

  const progressBar = (
    <div className="w-full">
      {/* Labels above bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {tierProgress.current && (
            <span
              className="loyalty-tag-skew text-xs font-bold px-2 py-0.5 rounded-full text-white"
              style={{ backgroundColor: tierProgress.current.color }}
            >
              {tierProgress.current.name}
            </span>
          )}
        </div>
        {tierProgress.next && (
          <span className="text-xs text-[var(--color-text-muted)]">
            Next: <span className="font-semibold" style={{ color: tierProgress.next.color }}>{tierProgress.next.name}</span>
          </span>
        )}
      </div>

      {/* Progress track */}
      <div className="w-full h-3 rounded-full bg-[var(--color-surface-border)] overflow-hidden">
        <div
          className={`loyalty-progress-bar h-full ${showGlow ? 'loyalty-progress-glow' : ''}`}
          style={{
            width: `${percent}%`,
            backgroundColor: tierProgress.current?.color ?? 'var(--color-primary)',
            '--glow-color': tierProgress.current?.color ?? 'var(--color-primary)',
          } as React.CSSProperties}
        />
      </div>

      {/* Stats below bar */}
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-[var(--color-text-muted)]">
          {percent.toFixed(0)}% complete
        </span>
        {tierProgress.pointsNeeded > 0 && (
          <span className="text-xs text-[var(--color-text-muted)]">
            <span className="font-semibold text-[var(--color-text)]">
              {tierProgress.pointsNeeded.toLocaleString()}
            </span>{' '}
            points to go
          </span>
        )}
      </div>
    </div>
  );

  const nextRewardCard = showNextRewardCard && tierProgress.next && (
    <div
      className="loyalty-card p-4 border-l-4"
      style={{ borderLeftColor: tierProgress.next.color }}
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
        Next Reward
      </p>
      <p className="text-sm font-bold text-[var(--color-text)]">
        {props.nextRewardTitle ?? `Unlock ${tierProgress.next.name}`}
      </p>
      {props.nextRewardDescription && (
        <p className="text-xs text-[var(--color-text-muted)] mt-1">
          {props.nextRewardDescription}
        </p>
      )}
    </div>
  );

  if (layout === 'compact') {
    return (
      <div className="loyalty-card p-4 space-y-3">
        {progressBar}
        {nextRewardCard}
      </div>
    );
  }

  // Horizontal layout
  return (
    <div className="loyalty-card p-6">
      <div className={`flex flex-col ${showNextRewardCard && tierProgress.next ? 'sm:flex-row' : ''} gap-6`}>
        <div className="flex-1">
          {progressBar}
        </div>
        {nextRewardCard && (
          <div className="sm:w-56 shrink-0">
            {nextRewardCard}
          </div>
        )}
      </div>
    </div>
  );
}
